import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { moduleAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { getDefaultSidebar, buildGroupedSidebar, FIXED_CORE_MODULES } from '../config/sidebarMenu';
import { canViewSidebarModule } from '../constants/permissions';

export interface SidebarModule {
  key: string;
  label: string;
  group: string;
  icon: string;
  type: 'core' | 'custom';
  apiPath?: string;
  path: string;
  sortOrder: number;
  isCore?: boolean;
  parentKey?: string;
}

interface ModuleContextType {
  modules: SidebarModule[];
  grouped: Record<string, SidebarModule[]>;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  refreshModules: () => Promise<void>;
  loading: boolean;
  isModuleEnabled: (moduleKey: string) => boolean;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

const pathForModule = (mod: { key: string; type: string }) => {
  const fixed = FIXED_CORE_MODULES.find((m) => m.key === mod.key);
  if (fixed) return fixed.path;
  if (mod.type === 'custom') return `/crm/${mod.key}`;
  return `/${mod.key}`;
};

const VEHICLE_FLEET_REF_KEYS = new Set(['vehicle_types', 'vehicle_makes', 'vehicle_models']);
const BILTY_CHILD_KEYS = new Set(['bilty_items', 'consignors', 'consignees', 'invoices']);
const GPS_CHILD_KEYS = new Set(['gps_devices', 'gps_alerts', 'gps_reports']);
const ALWAYS_ON_SIDEBAR_KEYS = new Set(['dashboard', 'settings', 'activity']);

function isSidebarModuleEnabled(moduleKey: string, enabledKeys: Set<string>): boolean {
  const fixed = FIXED_CORE_MODULES.find((m) => m.key === moduleKey);
  if (ALWAYS_ON_SIDEBAR_KEYS.has(moduleKey)) return true;
  if (enabledKeys.has(moduleKey)) return true;
  if (VEHICLE_FLEET_REF_KEYS.has(moduleKey)) {
    return enabledKeys.has('vehicles');
  }
  if (moduleKey === 'vehicle_documents') {
    return enabledKeys.has('vehicles') && enabledKeys.has('documents');
  }
  if (moduleKey === 'vehicle_vendors') {
    return enabledKeys.has('vehicle_vendors') || enabledKeys.has('vehicles');
  }
  if (BILTY_CHILD_KEYS.has(moduleKey)) {
    return enabledKeys.has('bilty');
  }
  if (GPS_CHILD_KEYS.has(moduleKey)) {
    return enabledKeys.has('gps_tracking');
  }
  if (moduleKey === 'fleet_reports') {
    return enabledKeys.has('reports') || enabledKeys.has('fleet_reports');
  }
  if (fixed?.parentKey) {
    if (!enabledKeys.has(fixed.parentKey)) return false;
    return enabledKeys.has(moduleKey);
  }
  return false;
}

export const ModuleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, isSuperAdmin, hasPermission } = useAuth();
  const defaults = getDefaultSidebar();
  const [modules, setModules] = useState<SidebarModule[]>(defaults.modules);
  const [grouped, setGrouped] = useState<Record<string, SidebarModule[]>>(defaults.grouped);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enabledKeys, setEnabledKeys] = useState<Set<string>>(new Set(defaults.modules.map((m) => m.key)));

  const isModuleEnabled = useCallback(
    (moduleKey: string) => isSidebarModuleEnabled(moduleKey, enabledKeys),
    [enabledKeys],
  );

  const refreshModules = useCallback(async () => {
    if (!token || isSuperAdmin) return;

    setLoading(true);
    try {
      const res = await moduleAPI.getSidebar();
      const apiModules: SidebarModule[] = (res.data.modules || []).map((m: SidebarModule, i: number) => ({
        ...m,
        path: pathForModule(m),
        isCore: m.type === 'core' || FIXED_CORE_MODULES.some((f) => f.key === m.key),
        sortOrder: m.sortOrder ?? i,
      }));

      const tenantEnabled = new Set(apiModules.map((m) => m.key));
      setEnabledKeys(tenantEnabled);

      const merged = FIXED_CORE_MODULES.filter((fixed) => isSidebarModuleEnabled(fixed.key, tenantEnabled))
        .map((fixed, i) => {
          const fromApi = apiModules.find((m) => m.key === fixed.key);
          return {
            ...fixed,
            // Always use friendly fixed labels for core menu (ignore old API names)
            label: fixed.label,
            group: fixed.group,
            parentKey: fixed.parentKey,
            sortOrder: i,
            path: fixed.path,
            type: fixed.type,
            isCore: fixed.type === 'core',
          };
        })
        .filter((m) => canViewSidebarModule(m.key, m.type, hasPermission));

      setModules(merged);
      setGrouped(buildGroupedSidebar(merged));
      setCollapsed(res.data.collapsed || false);
    } catch {
      const fallback = getDefaultSidebar();
      const filteredModules = fallback.modules.filter((m) =>
        canViewSidebarModule(m.key, m.type, hasPermission));
      setModules(filteredModules);
      setGrouped(buildGroupedSidebar(filteredModules));
      setEnabledKeys(new Set(filteredModules.map((m) => m.key)));
    } finally {
      setLoading(false);
    }
  }, [token, isSuperAdmin, hasPermission]);

  useEffect(() => { refreshModules(); }, [refreshModules]);

  return (
    <ModuleContext.Provider value={{
      modules, grouped, collapsed, setCollapsed, mobileOpen, setMobileOpen, refreshModules, loading, isModuleEnabled,
    }}>
      {children}
    </ModuleContext.Provider>
  );
};

export const useModules = () => {
  const ctx = useContext(ModuleContext);
  if (!ctx) throw new Error('useModules requires ModuleProvider');
  return ctx;
};
